import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { TextareaContentEditable } from '@dndbeyond/character-common-components/es';
import {
    Background,
    BackgroundCharacteristicContract,
    characterActions,
    CharacterTraits,
    Constants,
    RuleDataUtils,
    rulesEngineSelectors,
} from '@dndbeyond/character-rules-engine/es';

import { toastMessageActions } from '../../../actions/toastMessage';
import { SidebarHeader, SidebarSubheading } from '../../../components/Sidebar';
import SuggestionTable from '../../../components/SuggestionTable';
import { ThemeButton } from '../../../components/common/Button';
import { SharedAppState } from '../../../stores/typings';

interface Props extends DispatchProp {
    type: Constants.TraitTypeEnum;
    background: Background | null;
    traits: CharacterTraits;
    placeholder: string;
    autoSaveDelay: number;
}
interface State {
    type: string | null;
    hasBeenEdited: boolean;
    isSaveDirty: boolean;
}
class TraitPane extends React.PureComponent<Props, State> {
    static defaultProps = {
        placeholder: 'Enter any traits here!',
        autoSaveDelay: 5000,
    };

    autoSaveTimeoutId: number | null = null;
    textareaInput = React.createRef<TextareaContentEditable>();

    constructor(props) {
        super(props);

        this.state = {
            type: props.type,
            hasBeenEdited: false,
            isSaveDirty: false,
        };
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { type } = this.props;

        if (type !== prevProps.type) {
            this.setState({
                type,
                hasBeenEdited: false,
                isSaveDirty: false,
            });
        }
    }

    componentDidMount() {
        if (this.textareaInput.current) {
            this.textareaInput.current.focus();
        }
    }

    componentWillUnmount() {
        const { dispatch, type } = this.props;
        const { isSaveDirty } = this.state;

        this.handleStopAutoSaveTimer();

        if (isSaveDirty && this.textareaInput.current) {
            dispatch(characterActions.traitSet(type, this.textareaInput.current.getTextValue()));
        }
    }

    startAutoSaveTimer = (): void => {
        const { autoSaveDelay } = this.props;

        if (this.autoSaveTimeoutId === null) {
            this.autoSaveTimeoutId = window.setTimeout(() => {
                this.handleSave();
            }, autoSaveDelay);
        }
    };

    getContent = (): string => {
        const { type, traits } = this.props;

        let content = traits[type];

        return content ? content : '';
    };

    handleStopAutoSaveTimer = (): void => {
        if (this.autoSaveTimeoutId !== null) {
            window.clearTimeout(this.autoSaveTimeoutId);
            this.autoSaveTimeoutId = null;
        }
    };

    handleSave = (): void => {
        const { dispatch, type } = this.props;

        if (this.textareaInput.current) {
            dispatch(characterActions.traitSet(type, this.textareaInput.current.getTextValue()));
        }

        this.handleStopAutoSaveTimer();
        this.setState({
            isSaveDirty: false,
        });
    };

    handleInputKeyUp = (): void => {
        const { isSaveDirty } = this.state;

        if (!isSaveDirty) {
            this.setState(
                {
                    isSaveDirty: true,
                    hasBeenEdited: true,
                },
                this.startAutoSaveTimer,
            );
        }
    };

    handleInputBlur = (text: string): void => {
        this.handleSave();
    };

    handleSuggestionUse = (idx: number, diceRoll: number, description: string): void => {
        const { type, dispatch } = this.props;

        let newTraitContent = this.getContent();
        if (newTraitContent.length) {
            newTraitContent += '\n';
        }
        newTraitContent += description;

        dispatch(characterActions.traitSet(type, newTraitContent));
        dispatch(
            toastMessageActions.toastSuccess(
                `${RuleDataUtils.getTraitTypeName(type)} added to Personal Characteristics section`,
                `${diceRoll} - ${description.length > 70 ? description.substr(0, 70) + '...' : description}`,
            ),
        );

        if (this.textareaInput.current) {
            this.textareaInput.current.forceUpdateContent(newTraitContent);
        }
    };

    renderSuggestionList = (): React.ReactNode => {
        const { type, background } = this.props;

        if (type === Constants.TraitTypeEnum.APPEARANCE) {
            return null;
        }

        let suggestionNode: React.ReactNode;
        if (background && background.definition && background.definition[type]) {
            let suggestionData: Array<BackgroundCharacteristicContract> | null = background.definition[type];
            if (suggestionData !== null) {
                suggestionNode = (
                    <SuggestionTable
                        tableLabel={RuleDataUtils.getTraitTypeName(type)}
                        dieLabel={`d${suggestionData.length}`}
                        suggestions={suggestionData}
                        onSuggestionUse={this.handleSuggestionUse}
                    />
                );
            }
        } else {
            suggestionNode = (
                <div className="ct-trait-pane__suggestions-missing">
                    <p>Choose a background to get suggestions.</p>
                </div>
            );
        }

        return (
            <div className="ct-trait-pane__suggestions">
                <SidebarSubheading>Suggestions</SidebarSubheading>
                {suggestionNode}
            </div>
        );
        //`
    };

    render() {
        const { isSaveDirty, hasBeenEdited } = this.state;
        const { type, placeholder } = this.props;

        let calloutNode: React.ReactNode;
        if (hasBeenEdited) {
            if (isSaveDirty) {
                calloutNode = (
                    <React.Fragment>
                        <div className="ct-trait-pane__status ct-trait-pane__status--dirty">
                            <div className="ct-trait-pane__status-text">Unsaved changes</div>
                        </div>
                        <ThemeButton size="small" onClick={this.handleSave}>
                            Save
                        </ThemeButton>
                    </React.Fragment>
                );
            } else {
                calloutNode = (
                    <div className="ct-trait-pane__status ct-trait-pane__status--clean">
                        <div className="ct-trait-pane__status-icon" />
                        <div className="ct-trait-pane__status-text">Saved</div>
                    </div>
                );
            }
        }

        return (
            <div className="ct-trait-pane">
                <SidebarHeader callout={calloutNode}>{RuleDataUtils.getTraitTypeName(type)}</SidebarHeader>
                <div className="ct-trait-pane__content">
                    <TextareaContentEditable
                        key={type}
                        ref={this.textareaInput}
                        className="ct-trait-pane__input"
                        placeholder={placeholder}
                        initialContent={this.getContent()}
                        onInputKeyUp={this.handleInputKeyUp}
                        onInputBlur={this.handleInputBlur}
                    />
                </div>
                {this.renderSuggestionList()}
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        background: rulesEngineSelectors.getBackgroundInfo(state),
        traits: rulesEngineSelectors.getCharacterTraits(state),
    };
}

export default connect(mapStateToProps)(TraitPane);
